/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from './../../message';
import { IDescriptorBody } from '../../descriptor';
import { IRevisionObject } from '../revision';
import RevisionDescriptor, { IRevisionJSON, IRevision } from '../descriptors/revisiondescriptor';

export interface IRevisionHistoryUpdateRevisions {
	documentId: string;
	requestId: number;
	revisions: IRevisionObject[];
}

export interface IRevisionHistoryUpdateRevisionsMessageJSON extends IMessage {
	documentId: string;
	requestId: number;
	revisions: IRevisionJSON[];
}

export default class RevisionHistoryUpdateRevisionsMessage extends Message {
	public static readonly TYPE: string = '124';

	public static readonly READABLE_TYPE_NAME: string = 'updateRevisions';

	public static readonly DESCRIPTOR_NAME: string = 'RevisionHistoryUpdateRevisionsMessage';

	public static readonly DESCRIPTOR: IDescriptorBody = {
		fields: {
			documentId: {
				type: 'string',
				id: 1
			},
			requestId: {
				type: 'uint32',
				id: 2
			},
			revisions: {
				type: 'RevisionDescriptor',
				id: 3,
				rule: 'repeated'
			}
		}
	};

	public constructor(
		public readonly documentId: string,
		public readonly requestId: number,
		public readonly revisions: IRevision[]
	) {
		super();
	}

	public toJSON(): IRevisionHistoryUpdateRevisionsMessageJSON {
		return {
			documentId: this.documentId,
			requestId: this.requestId,
			revisions: this.revisions.map( RevisionDescriptor.toJSON )
		};
	}

	/**
	 * Creates frontend revision history model array from message sent from the backend.
	 *
	 * This method is intended to be used on the frontend side after receiving the broadcast message from the backend.
	 */
	public toObject(): IRevisionHistoryUpdateRevisions {
		return {
			documentId: this.documentId,
			requestId: this.requestId,
			revisions: this.revisions.map( RevisionDescriptor.toObject )
		};
	}

	/**
	 * Creates RevisionHistoryUpdateRevisionsMessage from frontend revision model array.
	 *
	 * This method is intended to be used on the frontend to create RevisionHistoryUpdateRevisionsMessage.
	 *
	 * This is optimization to prevent recreating the same revision models on the backend side
	 * as we just save these data in the database without need to parse them.
	 */
	public static create( params: IRevisionHistoryUpdateRevisions ): RevisionHistoryUpdateRevisionsMessage {
		return new RevisionHistoryUpdateRevisionsMessage(
			params.documentId,
			params.requestId,
			params.revisions.map( RevisionDescriptor.create )
		);
	}

	public static fromJSON( json: IRevisionHistoryUpdateRevisionsMessageJSON ): RevisionHistoryUpdateRevisionsMessage {
		return new RevisionHistoryUpdateRevisionsMessage(
			json.documentId,
			json.requestId,
			json.revisions.map( RevisionDescriptor.fromJSON )
		);
	}
}
